import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  EyeIcon, 
  PencilIcon, 
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CurrencyRupeeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import api from '../../api/axios';
import supabase from '../../api/supabase';
import { toast } from 'react-hot-toast';
import CustomerManagement from './CustomerManagement';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [payments, setPayments] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('phase');
  const [viewAsCustomer, setViewAsCustomer] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [assigningCustomer, setAssigningCustomer] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // Form states
  const [newPhase, setNewPhase] = useState({ name: '', status: 'pending', progress: 0, start_date: '', end_date: '' });
  const [newPhoto, setNewPhoto] = useState({ caption: '', description: '', phase: '', files: [] });
  const [newPayment, setNewPayment] = useState({ amount: '', description: '', due_date: '', status: 'pending', payment_type: '' });

  const PHASE_STATUSES = ['Not Started', 'In Progress', 'On Hold', 'Completed'];
  const [phaseEdits, setPhaseEdits] = useState({}); // { [index]: { status, progress, notes } }

  // Add filter state
  const [phaseFilter, setPhaseFilter] = useState('all');

  // Add state for delete confirmation
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  const tabs = [
    { id: 'overview', name: 'Overview', icon: EyeIcon },
    { id: 'phases', name: 'Phases', icon: CheckCircleIcon },
    { id: 'timeline', name: 'Timeline', icon: CalendarDaysIcon },
    { id: 'photos', name: 'Photos', icon: PhotoIcon },
    { id: 'payments', name: 'Payments', icon: CreditCardIcon },
    { id: 'support', name: 'Support', icon: ChatBubbleLeftRightIcon }
  ];

  useEffect(() => {
    fetchProjectDetails();
    
    // Set up real-time subscriptions
    const projectSubscription = supabase
      .channel('project-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'projects', filter: `id=eq.${id}` },
        (payload) => {
          console.log('Project real-time update:', payload);
          if (payload.eventType === 'UPDATE') {
            setProject(payload.new);
            toast.success('Project updated in real-time!');
          }
        }
      )
      .subscribe();

    const photosSubscription = supabase
      .channel('photos-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'site_photos', filter: `project_id=eq.${id}` },
        (payload) => {
          console.log('Photos real-time update:', payload);
          if (payload.eventType === 'INSERT') {
            setPhotos(prev => [payload.new, ...prev]);
            toast.success('New photo uploaded!');
          } else if (payload.eventType === 'UPDATE') {
            setPhotos(prev => prev.map(photo => photo.id === payload.new.id ? payload.new : photo));
            toast.success('Photo updated!');
          } else if (payload.eventType === 'DELETE') {
            setPhotos(prev => prev.filter(photo => photo.id !== payload.old.id));
            toast.success('Photo deleted!');
          }
        }
      )
      .subscribe();

    const paymentsSubscription = supabase
      .channel('payments-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'payments', filter: `project_id=eq.${id}` },
        (payload) => {
          console.log('Payments real-time update:', payload);
          if (payload.eventType === 'INSERT') {
            setPayments(prev => [payload.new, ...prev]);
            toast.success('New payment added!');
          } else if (payload.eventType === 'UPDATE') {
            setPayments(prev => prev.map(payment => payment.id === payload.new.id ? payload.new : payment));
            toast.success('Payment updated!');
          } else if (payload.eventType === 'DELETE') {
            setPayments(prev => prev.filter(payment => payment.id !== payload.old.id));
            toast.success('Payment deleted!');
          }
        }
      )
      .subscribe();

    const supportSubscription = supabase
      .channel('support-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'support', filter: `project_id=eq.${id}` },
        (payload) => {
          console.log('Support real-time update:', payload);
          if (payload.eventType === 'INSERT') {
            setSupportTickets(prev => [payload.new, ...prev]);
            toast.success('New support ticket created!');
          } else if (payload.eventType === 'UPDATE') {
            setSupportTickets(prev => prev.map(ticket => ticket.id === payload.new.id ? payload.new : ticket));
            toast.success('Support ticket updated!');
          } else if (payload.eventType === 'DELETE') {
            setSupportTickets(prev => prev.filter(ticket => ticket.id !== payload.old.id));
            toast.success('Support ticket deleted!');
          }
        }
      )
      .subscribe();

    // Fetch customers for assignment
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/admin/customers');
        setCustomers(response.data.data || []);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    };
    fetchCustomers();

    // Cleanup subscriptions on unmount
    return () => {
      projectSubscription.unsubscribe();
      photosSubscription.unsubscribe();
      paymentsSubscription.unsubscribe();
      supportSubscription.unsubscribe();
    };
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch project details
      const projectResponse = await api.get(`/admin/projects/${id}`);
      // Normalize phase statuses and calculate progress
      const statusMap = {
        pending: 'Not Started',
        'in-progress': 'In Progress',
        completed: 'Completed',
        'on-hold': 'On Hold',
      };
      const normalizedPhases = (projectResponse.data.data.phases || []).map(phase => ({
        ...phase,
        status: statusMap[phase.status] || phase.status
      }));
      // Calculate progress as percent of completed phases
      const progress = normalizedPhases.length > 0
        ? Math.round((normalizedPhases.filter(p => p.status === 'Completed').length / normalizedPhases.length) * 100)
        : 0;
      const normalizedProject = {
        ...projectResponse.data.data,
        phases: normalizedPhases,
        progress,
      };
      setProject(normalizedProject);
      
      // Fetch related data
      const [photosRes, paymentsRes, supportRes] = await Promise.all([
        api.get(`/admin/site-photos?project_id=${id}`),
        api.get(`/admin/payments?project_id=${id}`),
        api.get(`/admin/support?project_id=${id}`)
      ]);
      
      setPhotos(photosRes.data.data || []);
      setPayments(paymentsRes.data.data || []);
      setSupportTickets(supportRes.data.data || []);
      
    } catch (err) {
      console.error('Failed to fetch project details:', err);
      setError('Failed to load project details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhase = async (e) => {
    e.preventDefault();
    try {
      const updatedProject = {
        ...project,
        phases: [...(project.phases || []), newPhase]
      };
      const response = await api.put(`/admin/projects/${id}`, updatedProject);
      setProject(response.data.data);
      setShowAddModal(false);
      setNewPhase({ name: '', status: 'pending', progress: 0, start_date: '', end_date: '' });
    } catch (err) {
      console.error('Failed to add phase:', err);
      alert('Failed to add phase. Please try again.');
    }
  };

  const handleAddPhoto = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('project_id', id);
      formData.append('phase', newPhoto.phase);
      formData.append('caption', newPhoto.caption);
      formData.append('description', newPhoto.description);
      
      newPhoto.files.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post('/admin/site-photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPhotos(prev => [response.data.data, ...prev]);
      setShowAddModal(false);
      setNewPhoto({ caption: '', description: '', phase: '', files: [] });
    } catch (err) {
      console.error('Failed to add photo:', err);
      alert('Failed to add photo. Please try again.');
    }
  };

  // Utility to format numbers in Indian system (e.g., 1 crore 30 lakh)
  function formatIndianPrice(num) {
    if (!num) return 'N/A';
    num = Number(num);
    if (isNaN(num)) return num;
    if (num >= 10000000) {
      const crore = Math.floor(num / 10000000);
      const lakh = Math.floor((num % 10000000) / 100000);
      return `${crore} crore${lakh ? ' ' + lakh + ' lakh' : ''}`;
    } else if (num >= 100000) {
      const lakh = Math.floor(num / 100000);
      const thousand = Math.floor((num % 100000) / 1000);
      return `${lakh} lakh${thousand ? ' ' + thousand + ' thousand' : ''}`;
    } else if (num >= 1000) {
      const thousand = Math.floor(num / 1000);
      return `${thousand} thousand`;
    }
    return num.toString();
  }

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      const paymentData = {
        project_id: id,
        customer_id: project.customer,
        amount: newPayment.amount,
        description: newPayment.description,
        due_date: newPayment.due_date,
        status: newPayment.status,
        payment_method: 'bank-transfer',
        currency: 'INR',
        payment_type: newPayment.payment_type
      };
      const response = await api.post('/admin/payments', paymentData);
      setPayments(prev => [response.data.data, ...prev]);
      setShowAddModal(false);
      setNewPayment({ amount: '', description: '', due_date: '', status: 'pending', payment_type: '' });
    } catch (err) {
      console.error('Failed to add payment:', err);
      alert('Failed to add payment. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'planning': 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-green-100 text-green-800',
      'on-hold': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-purple-100 text-purple-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.planning;
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Handler for editing a phase field
  const handlePhaseEdit = (index, field, value) => {
    setPhaseEdits(prev => {
      const prevEdit = prev[index] || {};
      let newEdit = { ...prevEdit, [field]: value };
      if (field === 'status') {
        if (value === 'In Progress') {
          // If progress is less than 50, set to 50
          const currentProgress = prevEdit.progress !== undefined ? prevEdit.progress : (project.phases[index].progress || 0);
          newEdit.progress = currentProgress < 50 ? 50 : currentProgress;
        } else if (value === 'Completed') {
          newEdit.progress = 100;
        } else if (value === 'Not Started' || value === 'Pending') {
          newEdit.progress = 0;
        } else if (value === 'On Hold') {
          // Keep current progress when put on hold
          const currentProgress = prevEdit.progress !== undefined ? prevEdit.progress : (project.phases[index].progress || 0);
          newEdit.progress = currentProgress;
        }
      }
      return {
        ...prev,
        [index]: newEdit
      };
    });
  };

  // Handler for saving a phase edit
  const handleSavePhase = async (index) => {
    try {
      const edits = phaseEdits[index] || {};
      const updatedPhases = project.phases.map((phase, i) =>
        i === index ? { ...phase, ...edits } : phase
      );
      const updatedProject = { ...project, phases: updatedPhases };
      console.log('🔄 Admin updating phase:', { index, edits, updatedPhases });
      const response = await api.put(`/admin/projects/${id}`, updatedProject);
      console.log('✅ Admin phase update response:', response.data);
      setProject(response.data.data);
      setPhaseEdits(prev => ({ ...prev, [index]: undefined }));
      toast.success('Phase updated!');
    } catch (err) {
      console.error('❌ Admin failed to update phase:', err);
      toast.error('Failed to update phase. Please try again.');
    }
  };

  const handleDeletePhase = (index) => {
    if (!window.confirm('Are you sure you want to delete this phase?')) return;
    const updatedPhases = project.phases.filter((_, i) => i !== index);
    const updatedProject = { ...project, phases: updatedPhases };
    api.put(`/admin/projects/${id}`, updatedProject)
      .then(response => {
        setProject(response.data.data);
        toast.success('Phase deleted!');
      })
      .catch(err => {
        console.error('Failed to delete phase:', err);
        toast.error('Failed to delete phase. Please try again.');
      });
  };

  const handleMovePhase = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= project.phases.length) return;
    const phasesCopy = [...project.phases];
    const [moved] = phasesCopy.splice(index, 1);
    phasesCopy.splice(newIndex, 0, moved);
    // Update sort_order if needed
    phasesCopy.forEach((p, i) => p.sort_order = i + 1);
    const updatedProject = { ...project, phases: phasesCopy };
    api.put(`/admin/projects/${id}`, updatedProject)
      .then(response => {
        setProject(response.data.data);
        toast.success('Phase order updated!');
      })
      .catch(err => {
        console.error('Failed to reorder phases:', err);
        toast.error('Failed to reorder phases. Please try again.');
      });
  };

  // Add quick action handlers
  const handleQuickStart = async (index) => {
    try {
      const updatedPhases = project.phases.map((phase, i) =>
        i === index ? { ...phase, status: 'In Progress', progress: 50 } : phase
      );
      const updatedProject = { ...project, phases: updatedPhases };
      const response = await api.put(`/admin/projects/${id}`, updatedProject);
      setProject(response.data.data);
      setPhaseEdits(prev => ({ ...prev, [index]: undefined }));
      toast.success('Phase started! Progress set to 50%');
    } catch (err) {
      console.error('Failed to start phase:', err);
      toast.error('Failed to start phase. Please try again.');
    }
  };

  const handleQuickComplete = async (index) => {
    try {
      const updatedPhases = project.phases.map((phase, i) =>
        i === index ? { ...phase, status: 'Completed', progress: 100 } : phase
      );
      const updatedProject = { ...project, phases: updatedPhases };
      const response = await api.put(`/admin/projects/${id}`, updatedProject);
      setProject(response.data.data);
      setPhaseEdits(prev => ({ ...prev, [index]: undefined }));
      toast.success('Phase completed! Progress set to 100%');
    } catch (err) {
      console.error('Failed to complete phase:', err);
      toast.error('Failed to complete phase. Please try again.');
    }
  };

  const handleQuickHold = async (index) => {
    try {
      const currentPhase = project.phases[index];
      const updatedPhases = project.phases.map((phase, i) =>
        i === index ? { ...phase, status: 'On Hold' } : phase
      );
      const updatedProject = { ...project, phases: updatedPhases };
      const response = await api.put(`/admin/projects/${id}`, updatedProject);
      setProject(response.data.data);
      setPhaseEdits(prev => ({ ...prev, [index]: undefined }));
      toast.success('Phase put on hold! Progress maintained at current level.');
    } catch (err) {
      console.error('Failed to put phase on hold:', err);
      toast.error('Failed to put phase on hold. Please try again.');
    }
  };

  // Utility to calculate project status from phases (matches backend logic)
  function getProjectStatusFromPhases(phases) {
    if (!phases || phases.length === 0) return 'planning';
    const allCompleted = phases.every(p => p.status === 'Completed');
    const allNotStarted = phases.every(p => p.status === 'Not Started');
    const anyInProgressOrHold = phases.some(p => p.status === 'In Progress' || p.status === 'On Hold');
    if (allCompleted) return 'completed';
    if (allNotStarted) return 'planning';
    if (anyInProgressOrHold) return 'in-progress';
    // If there is a mix of Completed and Not Started, treat as in-progress
    const anyCompleted = phases.some(p => p.status === 'Completed');
    if (anyCompleted) return 'in-progress';
    return 'planning';
  }

  // Add delete handler
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await api.delete(`/admin/payments/${paymentId}`);
      setPayments(prev => prev.filter(p => p.id !== paymentId));
    } catch (err) {
      alert('Failed to delete payment. Please try again.');
    }
  };

  const handleAssignCustomer = async () => {
    if (!selectedCustomerId) {
      toast.error('Please select a customer to assign.');
      return;
    }
    setAssigningCustomer(true);
    try {
      await api.post(`/admin/projects/${project.id}/assign-customer`, { customer: selectedCustomerId });
      toast.success('Customer assigned successfully!');
      setProject(prev => ({ ...prev, customer: selectedCustomerId }));
      setSelectedCustomerId('');
    } catch (err) {
      console.error('Failed to assign customer:', err);
      toast.error('Failed to assign customer.');
    } finally {
      setAssigningCustomer(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Project Details...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Project</h2>
          <p className="text-gray-600 mb-4">{error || 'Project not found'}</p>
          <button 
            onClick={() => navigate('/admin/projects')} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/projects')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-display-bold text-gray-900">{project.name}</h1>
                <p className="text-gray-600">Project Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors duration-200 font-body-semibold text-sm flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add {modalType.charAt(0).toUpperCase() + modalType.slice(1)}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-palamuru-blue text-palamuru-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Project Header Card */}
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h2 className="text-xl font-display-semibold text-gray-900 mb-2">{project.name}</h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className={`px-2 py-1 rounded-full text-xs font-body-semibold ${getStatusColor(getProjectStatusFromPhases(project.phases))}`}>
                      {getProjectStatusFromPhases(project.phases).replace('-', ' ')}
                    </span>
                    <span className="flex items-center">
                      <MapPinIcon className="w-4 h-4 mr-1" />
                      {project.location_address}
                    </span>
                    <span className="flex items-center">
                      <CurrencyRupeeIcon className="w-4 h-4 mr-1" />
                      {project.budget_estimated?.toLocaleString() || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-display-bold text-gray-900">{project.progress || 0}%</div>
                  <div className="text-sm text-gray-600">Complete</div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mb-6">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${getProgressColor(project.progress || 0)} transition-all duration-500`}
                    style={{ width: `${project.progress || 0}%` }}
                  ></div>
                </div>
              </div>

              {/* Project Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h3 className="font-body-semibold text-gray-900">Project Info</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div>Start Date: {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}</div>
                    <div>Expected End: {project.expected_end_date ? new Date(project.expected_end_date).toLocaleDateString() : 'N/A'}</div>
                    <div>Building Type: {project.building_type || 'N/A'}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-body-semibold text-gray-900">Location</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div>{project.location_address || 'N/A'}</div>
                    <div>{project.location_city}, {project.location_state}</div>
                    <div>{project.location_zip_code}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-body-semibold text-gray-900">Quick Stats</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div>Phases: {project.phases?.length || 0}</div>
                    <div>Photos: {photos.length}</div>
                    <div>Payments: {payments.length}</div>
                    <div>Support Tickets: {supportTickets.length}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <h3 className="text-lg font-display-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-4">
                {project.activity_log?.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-palamuru-blue rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{activity.title}</div>
                      <div className="text-sm text-gray-600">{activity.description}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(activity.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'phases' && (
          <div className="space-y-6">
            {/* Phase Summary Card */}
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <h3 className="text-lg font-display-semibold text-gray-900 mb-4">Phase Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{project.phases?.length || 0}</div>
                  <div className="text-sm text-gray-600">Total Phases</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {project.phases?.filter(p => p.status === 'Not Started').length || 0}
                  </div>
                  <div className="text-sm text-blue-600">Pending</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {project.phases?.filter(p => p.status === 'In Progress').length || 0}
                  </div>
                  <div className="text-sm text-yellow-600">In Progress</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {project.phases?.filter(p => p.status === 'On Hold').length || 0}
                  </div>
                  <div className="text-sm text-orange-600">On Hold</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {project.phases?.filter(p => p.status === 'Completed').length || 0}
                  </div>
                  <div className="text-sm text-green-600">Completed</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                  <span className="text-sm font-bold text-gray-900">
                    {project.phases?.length > 0 
                      ? Math.round((project.phases.filter(p => p.status === 'Completed').length / project.phases.length) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-palamuru-blue h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${project.phases?.length > 0 
                        ? (project.phases.filter(p => p.status === 'Completed').length / project.phases.length) * 100
                        : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Phases Table */}
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-display-semibold text-gray-900">Project Phases</h3>
                  <p className="text-sm text-gray-600 mt-1">Manage and track project phases with quick actions</p>
                </div>
                <button
                  onClick={() => { setModalType('phase'); setShowAddModal(true); }}
                  className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors text-sm flex items-center gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Phase
                </button>
              </div>
              <div className="flex items-center justify-end mb-4">
                <label className="mr-2 text-sm text-gray-700 font-medium">Filter by Status:</label>
                <select
                  value={phaseFilter}
                  onChange={e => setPhaseFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue text-sm"
                >
                  <option value="all">All</option>
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phase Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quick Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {project.phases?.filter(phase => phaseFilter === 'all' || (phase.status || 'Not Started') === phaseFilter).map((phase, index) => {
                      const edit = phaseEdits[index] || {};
                      const status = edit.status || phase.status || 'Not Started';
                      const progress = edit.progress !== undefined ? edit.progress : (phase.progress || 0);
                      // Status badge color
                      const statusBadge = status === 'Completed'
                        ? 'bg-green-100 text-green-800'
                        : status === 'In Progress'
                          ? 'bg-blue-100 text-blue-800'
                          : status === 'On Hold'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-700';
                      return (
                        <tr key={index} className="bg-white align-top border-b-2 border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 text-sm text-gray-500 font-mono">{index + 1}</td>
                          <td className="px-4 py-4 text-sm font-body-semibold text-gray-900 w-48">
                            <input
                              type="text"
                              className="border rounded px-2 py-1 w-full"
                              value={edit.name !== undefined ? edit.name : phase.name}
                              onChange={e => handlePhaseEdit(index, 'name', e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-4 text-sm w-40">
                            <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${statusBadge} mb-2`}>{status}</div>
                            <select
                              className="border rounded px-2 py-1 w-full mt-1"
                              value={status}
                              onChange={e => handlePhaseEdit(index, 'status', e.target.value)}
                            >
                              {PHASE_STATUSES.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-4 text-sm w-48">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${getProgressColor(progress)} transition-all duration-500`}
                                  style={{ width: `${progress}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-mono w-10 text-right">{progress}%</span>
                            </div>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              className="border rounded px-2 py-1 w-full"
                              value={progress}
                              onChange={e => handlePhaseEdit(index, 'progress', Math.max(0, Math.min(100, Number(e.target.value))))}
                            />
                          </td>
                          <td className="px-4 py-4 text-sm w-36">
                            <input
                              type="date"
                              className="border rounded px-2 py-1 w-full"
                              value={edit.start_date !== undefined ? edit.start_date : (phase.start_date || '')}
                              onChange={e => handlePhaseEdit(index, 'start_date', e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-4 text-sm w-36">
                            <input
                              type="date"
                              className="border rounded px-2 py-1 w-full"
                              value={edit.end_date !== undefined ? edit.end_date : (phase.end_date || '')}
                              onChange={e => handlePhaseEdit(index, 'end_date', e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-4 text-sm w-64">
                            <textarea
                              className="border rounded px-2 py-1 w-full"
                              rows={1}
                              value={edit.notes !== undefined ? edit.notes : (phase.notes || '')}
                              onChange={e => handlePhaseEdit(index, 'notes', e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-4 text-sm flex flex-col gap-2 min-w-[120px]">
                            {status === 'Not Started' && (
                              <button
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs font-medium transition-colors"
                                onClick={() => handleQuickStart(index)}
                                disabled={loading}
                              >
                                Start Phase
                              </button>
                            )}
                            {status === 'In Progress' && (
                              <div className="flex flex-col gap-1">
                                <button
                                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs font-medium transition-colors"
                                  onClick={() => handleQuickComplete(index)}
                                  disabled={loading}
                                >
                                  Complete Phase
                                </button>
                                <button
                                  className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 text-xs font-medium transition-colors"
                                  onClick={() => handleQuickHold(index)}
                                  disabled={loading}
                                >
                                  Put On Hold
                                </button>
                              </div>
                            )}
                            {status === 'On Hold' && (
                              <button
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs font-medium transition-colors"
                                onClick={() => handleQuickStart(index)}
                                disabled={loading}
                              >
                                Resume Phase
                              </button>
                            )}
                            {status === 'Completed' && (
                              <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                ✓ Completed
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm flex flex-col gap-2 min-w-[120px]">
                            <div className="flex gap-2 mt-2">
                              <button
                                className="p-1 bg-palamuru-blue text-white rounded hover:bg-palamuru-blue-dark text-xs"
                                title="Save"
                                onClick={() => handleSavePhase(index)}
                                disabled={loading}
                              >
                                💾
                              </button>
                              <button
                                className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs"
                                title="Delete"
                                onClick={() => handleDeletePhase(index)}
                                disabled={loading}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-display-semibold text-gray-900">Project Timeline (Gantt View)</h3>
              </div>
              <div className="overflow-x-auto">
                <AdminGanttTimeline
                  phases={project.phases || []}
                  onEditPhase={index => { setModalType('phase'); setShowAddModal(true); setPhaseEdits({ [index]: project.phases[index] }); }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-display-semibold text-gray-900">Site Photos</h3>
                <button
                  onClick={() => { setModalType('photo'); setShowAddModal(true); }}
                  className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors text-sm"
                >
                  Upload Photos
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {photos.map((photo) => (
                  <div key={photo.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src={photo.file_url} 
                      alt={photo.caption || 'Site photo'} 
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h4 className="font-body-semibold text-gray-900 mb-2">{photo.caption || 'Untitled'}</h4>
                      <p className="text-sm text-gray-600 mb-2">{photo.description}</p>
                      <div className="text-xs text-gray-500">
                        {photo.phase && <div>Phase: {photo.phase}</div>}
                        <div>{new Date(photo.uploaded_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-display-semibold text-gray-900">Payments</h3>
                <button
                  onClick={() => { setModalType('payment'); setShowAddModal(true); }}
                  className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors text-sm"
                >
                  Add Payment
                </button>
              </div>
              
              <div className="space-y-4">
                {payments.map((payment) => (
                  <div key={payment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-body-semibold text-gray-900">₹{payment.amount?.toLocaleString()}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-body-semibold ${
                        payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {payment.status}
                      </span>
                      <button
                        className="ml-2 text-red-500 hover:text-red-700 text-lg"
                        title="Delete Payment"
                        onClick={() => handleDeletePayment(payment.id)}
                      >
                        🗑️
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>Phase: {payment.phase || 'N/A'}</div>
                      <div>Due: {payment.due_date ? new Date(payment.due_date).toLocaleDateString() : 'N/A'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <h3 className="text-lg font-display-semibold text-gray-900 mb-6">Support Tickets</h3>
              
              <div className="space-y-4">
                {supportTickets.map((ticket) => (
                  <div key={ticket.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-body-semibold text-gray-900">{ticket.subject}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-body-semibold ${
                        ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                        ticket.status === 'open' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{ticket.message}</p>
                    <div className="text-xs text-gray-500">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {project && !project.customer && (
          <div className="my-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold mb-2 text-yellow-800">Assign Customer to Project</h3>
            <select value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)} className="border p-2 rounded w-full mb-2">
              <option value="">Select Customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>{customer.full_name || customer.email}</option>
              ))}
            </select>
            <button onClick={handleAssignCustomer} disabled={assigningCustomer} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors">
              {assigningCustomer ? 'Assigning...' : 'Assign Customer'}
            </button>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Add {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
            </h2>
            
            {modalType === 'phase' && (
              <form onSubmit={handleAddPhase} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phase Name</label>
                  <input
                    type="text"
                    value={newPhase.name}
                    onChange={(e) => setNewPhase({...newPhase, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={newPhase.start_date}
                      onChange={(e) => setNewPhase({...newPhase, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={newPhase.end_date}
                      onChange={(e) => setNewPhase({...newPhase, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={newPhase.status}
                      onChange={(e) => setNewPhase({...newPhase, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Progress (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={newPhase.progress}
                      onChange={(e) => setNewPhase({...newPhase, progress: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors"
                  >
                    Add Phase
                  </button>
                </div>
              </form>
            )}

            {modalType === 'photo' && (
              <form onSubmit={handleAddPhoto} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                  <input
                    type="text"
                    value={newPhoto.caption}
                    onChange={(e) => setNewPhoto({...newPhoto, caption: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newPhoto.description}
                    onChange={(e) => setNewPhoto({...newPhoto, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
                  <select
                    value={newPhoto.phase}
                    onChange={(e) => setNewPhoto({...newPhoto, phase: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                  >
                    <option value="">Select Phase</option>
                    {project.phases?.map((phase, index) => (
                      <option key={index} value={phase.name}>{phase.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Files</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => setNewPhoto({...newPhoto, files: Array.from(e.target.files)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors"
                  >
                    Upload Photos
                  </button>
                </div>
              </form>
            )}

            {modalType === 'payment' && (
              <form onSubmit={handleAddPayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                  <select
                    value={newPayment.payment_type || ''}
                    onChange={e => setNewPayment({ ...newPayment, payment_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Advance">Advance</option>
                    <option value="General Payment">General Payment</option>
                    <option value="Closing Payment">Closing Payment</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={newPayment.amount}
                    onChange={e => setNewPayment({...newPayment, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    required
                  />
                  {newPayment.amount && (
                    <div className="text-xs text-gray-500 mt-1">
                      {formatIndianPrice(newPayment.amount)}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <input
                    type="text"
                    value={newPayment.description || ''}
                    onChange={e => setNewPayment({...newPayment, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={newPayment.due_date}
                    onChange={e => setNewPayment({...newPayment, due_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={newPayment.status}
                    onChange={e => setNewPayment({...newPayment, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-palamuru-blue"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-palamuru-blue-dark transition-colors"
                  >
                    Add Payment
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function AdminGanttTimeline({ phases, onEditPhase }) {
  if (!phases.length) return (
    <div className="text-center py-8">
      <div className="text-gray-400 text-6xl mb-4">📅</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No Phases Yet</h3>
      <p className="text-gray-600">Add phases to see the project timeline</p>
    </div>
  );

  // Calculate min/max dates
  const validDates = phases.filter(p => p.start_date && p.end_date);
  if (validDates.length === 0) return (
    <div className="text-center py-8">
      <div className="text-gray-400 text-6xl mb-4">📅</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No Date Information</h3>
      <p className="text-gray-600">Add start and end dates to phases to see the timeline</p>
    </div>
  );

  const minDate = new Date(Math.min(...validDates.map(p => new Date(p.start_date).getTime())));
  const maxDate = new Date(Math.max(...validDates.map(p => new Date(p.end_date).getTime())));
  const totalDays = Math.max(1, Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)));

  // Color by status
  const statusColor = status => {
    switch (status) {
      case 'Completed': return '#22c55e';
      case 'In Progress': return '#2563eb';
      case 'On Hold': return '#f97316';
      case 'Delayed': return '#ef4444';
      default: return '#a3a3a3';
    }
  };

  const statusBgColor = status => {
    switch (status) {
      case 'Completed': return 'bg-green-100';
      case 'In Progress': return 'bg-blue-100';
      case 'On Hold': return 'bg-orange-100';
      case 'Delayed': return 'bg-red-100';
      default: return 'bg-gray-100';
    }
  };

  return (
    <div className="w-full">
      {/* Timeline Header */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Timeline Overview</span>
          <span className="text-xs text-gray-500">
            {minDate.toLocaleDateString()} - {maxDate.toLocaleDateString()}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-palamuru-blue h-2 rounded-full transition-all duration-500"
            style={{ 
              width: `${Math.min(100, (phases.filter(p => p.status === 'Completed').length / phases.length) * 100)}%` 
            }}
          ></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{phases.filter(p => p.status === 'Not Started').length} Pending</span>
          <span>{phases.filter(p => p.status === 'In Progress').length} In Progress</span>
          <span>{phases.filter(p => p.status === 'On Hold').length} On Hold</span>
          <span>{phases.filter(p => p.status === 'Completed').length} Completed</span>
        </div>
      </div>

      {/* Timeline Bars */}
      <div className="space-y-4">
        {phases.map((phase, i) => {
          if (!phase.start_date || !phase.end_date) {
            return (
              <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-40 truncate text-sm font-medium text-gray-800">{phase.name}</div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">No dates set</span>
                </div>
                <button
                  onClick={() => onEditPhase(i)}
                  className="px-3 py-1 bg-palamuru-blue text-white rounded text-xs hover:bg-palamuru-blue-dark transition-colors"
                >
                  Set Dates
                </button>
              </div>
            );
          }

          const start = new Date(phase.start_date);
          const end = new Date(phase.end_date);
          const left = ((start - minDate) / (1000 * 60 * 60 * 24)) / totalDays * 100;
          const width = (Math.max(1, (end - start) / (1000 * 60 * 60 * 24) + 1) / totalDays) * 100;
          const isOverdue = end < new Date() && phase.status !== 'Completed';

          return (
            <div key={i} className="flex items-center gap-4 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow group">
              <div className="w-40 truncate text-sm font-medium text-gray-800">{phase.name}</div>
              <div className="relative flex-1 h-8">
                <div
                  className={`absolute top-2 h-4 rounded-full cursor-pointer shadow-md transition-all group-hover:scale-105 ${isOverdue ? 'animate-pulse' : ''}`}
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    background: statusColor(phase.status),
                    opacity: 0.9,
                  }}
                  title={`${phase.name}\nStatus: ${phase.status}\nProgress: ${phase.progress || 0}%\n${phase.start_date} to ${phase.end_date}`}
                  onClick={() => onEditPhase(i)}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs text-white font-bold drop-shadow-sm">
                      {phase.status === 'Completed' ? '✓' : 
                       phase.status === 'In Progress' ? '▶' : 
                       phase.status === 'On Hold' ? '⏸' : '○'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBgColor(phase.status)}`}>
                  {phase.status}
                </span>
                <span className="text-xs text-gray-500 font-mono">
                  {phase.progress || 0}%
                </span>
                <button
                  onClick={() => onEditPhase(i)}
                  className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 transition-colors opacity-0 group-hover:opacity-100"
                >
                  Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Timeline Legend */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Legend</h4>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
            <span>Not Started</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span>On Hold</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span>Overdue</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectDetails; 